import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize, isArray } from "lodash";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { HiOutlineEye } from "react-icons/hi";
import {
    PiBuildingOffice, PiCalendarBlank, PiChatCircle,
    PiMapPin,
    PiTruck,
    PiUser
} from 'react-icons/pi';
import { RiWeightLine } from "react-icons/ri";
import { TbCheck, TbCircleCheck, TbPrinter, TbReceipt, TbTicket, TbTimeline } from "react-icons/tb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { menuRoutes } from "../../../router/menu";
import { setSelectedView } from "../../../redux/pickupProfileSlice";
import { PickupService } from "../../../services/pickup/pickupService";
import { Pickup, PickupsApiResponse } from "../../../type/entities/pickup-type";
import cn from "../../../utils/classNames";
import { pickupStatuses } from "../../../utils/SelectorMapping";
import CompletePickupModal from "../components/CompletePickupModal";
import FailPickupModal from "../components/FailPickupModal";
import PickupsForm from "../components/PickupsForm";
import ReschedulePickupModal from "../components/ReschedulePickupModal";
import PickupsFilters from "./PickupsFilters";
import ValidatePickupModal from "../components/ValidatePickupModal";

interface PickupsListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    pickupSelected: Pickup | null;
    setPickupSelected: React.Dispatch<React.SetStateAction<Pickup | null>>;
    registerTableRefetch?: (refetch: () => void) => void;
}

const columnOrder = [
    'id',
    'headquarter',
    'user',
    'route',
    'vehicle',
    'company',
    'pickupStatus',
    'totalProducts',
    'pickedWeightKg',
    'comments',
    'pickupDate',
    'scheduledNext',
    'createdAt',
    'updatedAt'
];

const PickupsList: FC<PickupsListProps> = ({ openFormDrawer, setOpenFormDrawer, pickupSelected, setPickupSelected, registerTableRefetch }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [openCompleteModal, setOpenCompleteModal] = useState<boolean>(false);
    const [pickupToComplete, setPickupToComplete] = useState<Pickup | null>(null);
    const [openValidatePickupModal, setOpenValidatePickupModal] = useState<boolean>(false);
    const [idsToValidate, setIdsToValidate] = useState<string[]>([]);
    const [openFailModal, setOpenFailModal] = useState<boolean>(false);
    const [pickupToFail, setPickupToFail] = useState<Pickup | null>(null);
    const [openRescheduleModal, setOpenRescheduleModal] = useState<boolean>(false);
    const [idsToReschedule, setIdsToReschedule] = useState<string[]>([]);
    const [statusCounts, setStatusCounts] = useState<{[key: string]: number}>({});
    const service = new PickupService();

    // HOOKS
    const { userCan } = usePrivilege();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE RECOGIDAS
     * @EN GETS THE LIST OF PICKUPS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listPickups(filters);
        fetchStatusCounts();
        return response.getResponseData() as PickupsApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        registerTableRefetch?.(refetch);
    }, [registerTableRefetch, refetch]);

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL CONTEO DE RECOGIDAS POR ESTADO
     * @EN GETS THE COUNT OF PICKUPS BY STATUS
     */
    // -------------------------------------------------------------------------------------------------------------
    const fetchStatusCounts = useCallback(async () => {
        try {
            const response = await service.getPickupCountByStatus(filters);
            const responseData = response.getResponseData();
            if (responseData.success && responseData.data) {
                const counts: {[key: string]: number} = {};
                responseData.data.forEach((item: any) => {
                    counts[item.pickupStatus] = item.pickupsNumber;
                });
                setStatusCounts(counts);
            }
        } catch (error: any) {
            console.error('Error al obtener conteos por estado:', error);
        }
    }, []);
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UNA RECOGIDA
     * @EN DELETES A PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await service.deletePickup(id);
            const responseData = response.getResponseData();
            if (responseData.success) {
                refetch();
                toast.success(responseData.message || 'Recogida eliminada con éxito');
            } else {
                toast.error(responseData.message || 'Error al eliminar la recogida');
            }
        } catch (error: any) {
            toast.error(error.message || 'Error al eliminar la recogida');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA MÚLTIPLES RECOGIDAS
     * @EN DELETES MULTIPLE PICKUPS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultipleDelete = async (ids: string[]) => {
        try {
            if (ids.length === 0) {
                toast.error('No se ha seleccionado ninguna recogida válida para eliminar');
                return;
            }
            const response = await service.deleteMultiPickups(ids);
            const responseData = response.getResponseData();
            if (responseData.success) {
                refetch();
                toast.success(responseData.message || 'Recogidas eliminadas con éxito');
            } else {
                toast.error(responseData.message || 'Error al eliminar las recogidas');
            }
        } catch (error: any) {
            toast.error(error.message || 'Error al eliminar las recogidas');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA RECOGIDAS A EXCEL
     * @EN EXPORTS PICKUPS TO EXCEL
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleExportExcel = async () => {
        try {
            const response = await service.exportToExcel(filters);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `recogidas_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Archivo Excel descargado con éxito');
        } catch (error: any) {
            toast.error(error.message || 'Error al exportar a Excel');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGA EL RECIBO DE UNA RECOGIDA
     * @EN DOWNLOADS THE RECEIPT OF A PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDownloadReceipt = async (pickupId: string) => {
        try {
            const response = await service.generateReceipt(pickupId);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `recibo_recogida.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Recibo descargado con éxito');
        } catch (error: any) {
            toast.error(error.message || 'Error al descargar el recibo');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGA EL TICKET DE UNA RECOGIDA
     * @EN DOWNLOADS THE TICKET OF A PICKUP
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDownloadTicket = async (pickupId: string) => {
        try {
            const response = await service.generateTicket(pickupId);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ticket_recogida.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Ticket descargado con éxito');
        } catch (error: any) {
            toast.error(error.message || 'Error al descargar el ticket');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA EL TICKET DE UNA RECOGIDA PARA IMPRIMIR
     * @EN SHOWS THE TICKET OF A PICKUP FOR PRINTING
     */
    //-------------------------------------------------------------------------------------------------------------
    const handlePrintTicket = async (pickupId: string) => {
        try {
            const response = await service.generateTicket(pickupId);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Abrir en nueva ventana para imprimir
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            } else {
                toast.error('No se pudo abrir la ventana de impresión');
            }

            // Limpiar la URL después de un tiempo
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 1000);

            toast.success('Ticket abierto para impresión');
        } catch (error: any) {
            toast.error(error.message || 'Error al mostrar el ticket para imprimir');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {/* Filtos de estados con Buttons */}
            <div className="flex flex-wrap gap-2">
                <div className="flex flex-wrap bg-[#f48b210d] mb-3 p-2 rounded-lg">
                    <Button
                        type="button"
                        onClick={() => updateFilters({ pickup_status: null })}
                        className={cn("bg-transparent border-none text-gray-600", {
                            "bg-white": !filters?.filter_filters?.pickup_status || isArray(filters?.filter_filters?.pickup_status),
                        })}
                    >
                        Todos
                    </Button>
                    {pickupStatuses.map((status) => {
                        const isChecked = filters?.filter_filters?.pickup_status == status.value;
                        const count = statusCounts[status.value] || 0;
                        return (
                            <Button
                                key={status.value}
                                type="button"
                                onClick={() => updateFilters({ pickup_status: status.value })}
                                className={cn("bg-transparent border-none text-gray-600", {
                                    "bg-white": isChecked,
                                })}
                            >
                                {status.label} {count > 0 && 
                                    <span className="ml-1 px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-xs">
                                        {count}
                                    </span>
                                }
                            </Button>
                        )
                    }
                    )}
                </div>
            </div>

            {data !== undefined && (
                <CustomTable
                    id={'pickups-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['pickupStatus']}
                    columnsNotShown={['createdAt', 'scheduledNext']}
                    customRowsBackgroundColors={
                        pickupStatuses.map((status) => ({
                            column: 'pickupStatus',
                            values: [status.value],
                            gradientBg: status.gradientBg,
                            useGradient: true,
                        }))
                    }
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'pickupDate',
                            columnNameKey: 'Fecha de Recogida',
                            render: (row: any) => {
                                const status = row.row.original?.pickupStatus;
                                const date = row.row.original?.pickupDate?.date;
                                const nextDate = row.row.original?.scheduledNext?.date;
                                const isPlanned = status === 'planned' || status === 'rescheduled';

                                if (isPlanned) {
                                    const plannedDate = date ? moment(date) : null;
                                    return (
                                        <div className="text-sm text-gray-900">
                                            <span className="text-gray-500 block">Sin visita</span>
                                            {plannedDate && (
                                                <Tooltip content={`Planificada: ${plannedDate.format('DD/MM/YYYY HH:mm')}`}>
                                                    <span>{plannedDate.format('DD/MM/YYYY')}</span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    );
                                }

                                const formattedDate = date ? moment(date) : null;
                                return (
                                    <div className="text-sm text-gray-900">
                                        {formattedDate ? (
                                            <Tooltip content={formattedDate.format('DD/MM/YYYY HH:mm')}>
                                                <span>{formattedDate.format('DD/MM/YYYY')}</span>
                                            </Tooltip>
                                        ) : nextDate ? (
                                            <span className="text-gray-500">Sin visita</span>
                                        ) : (
                                            'N/A'
                                        )}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'headquarter',
                            columnNameKey: 'Sede',
                            render: (row: any) => {
                                const headquarter = row.row.original?.headquarter;
                                return (
                                    <div className="flex flex-col gap-1">
                                        <div className="font-bold flex items-center gap-2 text-sm text-gray-900">
                                            <PiBuildingOffice className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                            <span>{headquarter?.name || 'Sin asignar'}</span>
                                        </div>
                                        <div className="text-sm text-gray-500 text-nowrap">
                                            <span className="font-bold">Ciudad: </span>
                                            {headquarter?.city ?? 'N/A'}
                                        </div>
                                        <div className="text-sm text-gray-500 text-nowrap">
                                            <span className="font-bold">Dirección: </span>
                                            {headquarter?.address ?? 'N/A'}
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'route',
                            columnNameKey: 'Ruta',
                            render: (row: any) => {
                                const route = row.row.original?.route?.name;
                                const routeDescription = row.row.original?.route?.description;
                                return (
                                    <>
                                        <div className="font-bold flex items-center gap-2 text-sm text-gray-900">
                                            <PiMapPin className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                            <span className="text-nowrap">{route || 'Sin asignar'}</span>
                                        </div>
                                        {/* Add text ellipis for 1 line and  */}
                                        <span className="m-3 mt-1 text-xs text-gray-500 line-clamp-1">
                                            {routeDescription || 'N/A'}
                                        </span>
                                    </>
                                );
                            }
                        },
                        {
                            key: 'user',
                            columnNameKey: 'Usuario Asignado',
                            render: (row: any) => {
                                const user = row.row.original?.user;
                                const userName = user ? `${user.name} ${user.lastName || ''}`.trim() : null;
                                return (
                                    <div className="flex flex-col gap-1">
                                        <div className="font-bold flex items-center gap-2 text-sm text-gray-900">
                                            <PiUser className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                            <span>{userName || 'Sin asignar'}</span>
                                        </div>
                                        <div className="text-sm text-gray-500 text-nowrap">
                                            <span className="font-bold">DNI: </span>
                                            {user?.dni ?? 'N/A'}
                                        </div>
                                        <div className="text-sm text-gray-500 text-nowrap">
                                            <span className="font-bold">Email: </span>
                                            {user?.email ?? 'N/A'}
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'vehicle',
                            columnNameKey: 'Vehículo',
                            render: (row: any) => {
                                const vehicle = row.row.original?.vehicle;
                                const vehicleInfo = vehicle ? `${vehicle.brand} ${vehicle.model}` : null;
                                const fullInfo = vehicle ? `${vehicle.brand} ${vehicle.model} (${vehicle.plateNumber})` : null;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        {fullInfo ? (
                                            <div className="flex flex-col">
                                                <div className="font-bold text-nowrap gap-3 flex flex-row items-center">
                                                    <PiTruck className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                                    {vehicleInfo}
                                                </div>
                                                <div className="text-sm text-gray-500 text-nowrap">
                                                    <span className="font-bold">Matricula: </span>
                                                    {vehicle?.plateNumber ?? 'N/A'}
                                                </div>
                                            </div>
                                        ) : (
                                            <span>Sin asignar</span>
                                        )}
                                    </div>
                                );
                            }
                        },

                        {
                            key: 'pickupStatus',
                            columnNameKey: 'Estado',
                            render: (row: any) => {
                                const status = row.row.original?.pickupStatus;
                                const statusOption = pickupStatuses.find((item) => item.value === status);
                                if (!status) return 'Sin estado';
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center pointer-events-none' key={row.row.original.id + status}>
                                        <StatusDropdown
                                            entityId={row.row.original?.id}
                                            title="¿Estas seguro de cambiar el estado?"
                                            statesOptions={pickupStatuses}
                                            currentState={{
                                                name: statusOption?.label,
                                                id: statusOption?.value,
                                                color: statusOption?.color
                                            }}
                                            handleStateChange={(entityId: string, status: any, comment: string) => console.log(entityId, status, comment)}
                                            disabled={!userCan('edit', 'vehicle') || status === 'sold'}
                                        />
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'pickedWeightKg',
                            columnNameKey: 'Peso Recogido',
                            render: (row: any) => {
                                const weight = row.row.original?.pickedWeightKg;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <RiWeightLine className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                        <span className="font-medium">
                                            {weight !== null && weight !== undefined ? `${weight} kg` : 'N/A'}
                                        </span>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'comments',
                            columnNameKey: 'Comentarios',
                            render: (row: any) => {
                                const comments = row.row.original?.comments?.trim();
                                return (
                                    <div className="text-sm text-gray-500 max-w-xs">
                                        <div className="flex items-center gap-2">
                                            <PiChatCircle className="text-gray-400 flex-shrink-0" />
                                            <span className="line-clamp-3 max-w-[200px]">{comments || 'Sin comentarios'}</span>
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'scheduledNext',
                            columnNameKey: 'Próxima Recogida',
                            render: (row: any) => {
                                const date = row.row.original?.scheduledNext?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'No programada';
                            }
                        },
                        {
                            key: 'createdAt',
                            columnNameKey: 'Creado',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{moment(date).format('DD/MM/YYYY')}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'pickup', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver Datos'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.pickups.path}/${row.id}/profile/${menuRoutes.pickups.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Status History',
                            permissions: { group: 'pickup', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Historial de estados'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.pickups.path}/${row.id}/profile/${menuRoutes.pickups.profile.statusHistory}`}
                                            onClick={() => dispatch(setSelectedView(menuRoutes.pickups.profile.statusHistory)) }
                                        >            
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                            >
                                                <TbTimeline className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Reprogramar',
                            permissions: { group: 'pickup', permission: 'edit' },
                            hide: (row: any) => {
                                const status = row?.pickupStatus;
                                return status !== 'planned' && status !== 'rescheduled';
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Reprogramar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setIdsToReschedule([row.id]);
                                                setOpenRescheduleModal(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <PiCalendarBlank className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Complete',
                            permissions: { group: 'pickup', permission: 'execute' },
                            hide: (row: any) => {
                                const status = row?.pickupStatus;
                                return status !== 'planned' && status !== 'rescheduled' && status !== 'started_by_driver';
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Marcar como Realizada'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setPickupToComplete(row);
                                                setOpenCompleteModal(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-white text-white bg-green-700"
                                        >
                                            <TbCircleCheck className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Failed',
                            permissions: { group: 'pickup', permission: 'mark_failed' },
                            hide: (row: any) => {
                                const status = row?.pickupStatus;
                                return status !== 'planned' && status !== 'rescheduled' && status !== 'started_by_driver';
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Marcar como Fallida'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setPickupToFail(row);
                                                setOpenFailModal(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-white text-white bg-amber-400"
                                        >
                                            <FaXmark className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'pickup', permission: 'delete' },
                            hide: (row: any) => {
                                const status = row?.pickupStatus;
                                return status !== 'planned' && status !== 'rescheduled';
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar recogida`}
                                                description={`¿Estás seguro de que deseas eliminar la recogida?, será irreversible.`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Download Receipt',
                            permissions: { group: 'pickup', permission: 'generate_receipt' },
                            hide: (row: any) => {
                                const status = row?.pickupStatus;
                                return status !== 'completed' && status !== 'finished_by_driver' && status !== 'validated_by_admin' && status !== 'rejected_by_admin';
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Descargar Recibo'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => handleDownloadReceipt(row.id)}
                                            as="span"
                                            size="sm"
                                            variant="solid"
                                            className="hover:!border-gray-900 hover:text-white bg-sky-500"
                                        >
                                            <TbReceipt className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Download Ticket',
                            permissions: { group: 'pickup', permission: 'generate_receipt' },
                            hide: (row: any) => {
                                const status = row?.pickupStatus;
                                return status !== 'completed' && status !== 'finished_by_driver' && status !== 'validated_by_admin' && status !== 'rejected_by_admin';
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Descargar Ticket'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => handleDownloadTicket(row.id)}
                                            as="span"
                                            size="sm"
                                            variant="solid"
                                            className="hover:!border-gray-900 hover:text-white bg-orange-400"

                                        >
                                            <TbTicket className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Print Ticket',
                            permissions: { group: 'pickup', permission: 'generate_receipt' },
                            hide: (row: any) => {
                                const status = row?.pickupStatus;
                                return status !== 'completed' && status !== 'finished_by_driver' && status !== 'validated_by_admin' && status !== 'rejected_by_admin';
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Imprimir Ticket'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => handlePrintTicket(row.id)}
                                            as="span"
                                            size="sm"
                                            variant="solid"
                                            className="hover:!border-gray-900 text-gray-700 bg-white"
                                        >
                                            <TbPrinter className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    multiActions={[
                        {
                            label: 'Reprogramar',
                            permissions: { group: 'pickup', permission: 'edit' },
                            render: (selectedIds: string[], selectedOriginals: any[], resetSelection: Function) => {
                                const allowedStatuses = ['planned', 'rescheduled'];
                                const invalidRows = selectedOriginals.filter((o: any) => !allowedStatuses.includes(o?.pickupStatus));
                                const validIds = selectedOriginals.filter((o: any) => allowedStatuses.includes(o?.pickupStatus)).map((o: any) => o.id);
                                const hasInvalid = invalidRows.length > 0;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={hasInvalid ? "Solo se pueden reprogramar estados: 'planificada' o 'reprogramada'" : 'Reprogramar seleccionadas'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className={cn('hover:!border-gray-900 p-5 text-orange-500 border-orange-500')}
                                            onClick={() => {
                                                if (hasInvalid) {
                                                    toast.warning(`Se omitieron ${invalidRows.length} recogida/s por estado inválido. Solo 'Planificada' o 'Reprogramada'.`);
                                                }
                                                if (validIds.length > 0) {
                                                    setIdsToReschedule(validIds);
                                                    setOpenRescheduleModal(true);
                                                    resetSelection();
                                                }
                                            }}
                                        >
                                            Reprogramar
                                            <PiCalendarBlank className="ml-2 h-4 w-4 m-h-[15px] m-w-[15px]" />
                                        </Button>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: 'Administrar',
                            permissions: { group: 'pickup', permission: 'validate' },
                            render: (selectedIds: string[], selectedOriginals: any[], resetSelection: Function) => {
                                const allowedStatuses = ['finished_by_driver'];
                                const invalidRows = selectedOriginals.filter((o: any) => !allowedStatuses.includes(o?.pickupStatus));
                                const validIds = selectedOriginals.filter((o: any) => allowedStatuses.includes(o?.pickupStatus)).map((o: any) => o.id);
                                const hasInvalid = invalidRows.length > 0;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={hasInvalid ? "Solo se pueden administrar estados: 'planificada' o 'reprogramada'" : 'Administrar seleccionadas'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className={cn('hover:!border-gray-900 p-5 text-green-500 border-green-500')}
                                            onClick={() => { 
                                                if (hasInvalid) {
                                                    toast.warning(`Se omitieron ${invalidRows.length} recogida/s por estado inválido. Solo 'Finalizada por conductor'.`);
                                                }
                                                if (validIds.length > 0) {
                                                    setIdsToValidate(validIds);
                                                    setOpenValidatePickupModal(true);
                                                    resetSelection();
                                                }
                                            }}
                                        >
                                            Administrar
                                            <TbCheck className="ml-2 h-4 w-4 m-h-[15px] m-w-[15px]" />
                                        </Button>
                                    </Tooltip>
                                );
                            }
                        },
                    ]}
                    handleMultipleDelete={handleMultipleDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as PickupsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    {userCan('export', 'pickup') && (
                        <Button
                            variant="solid"
                            size="sm"
                            onClick={() => handleExportExcel()}
                            className="p-4 bg-green-600"
                        >
                            <FaFileExcel className="mr-2 min-h-[20px] min-w-[20px]" />
                            Exportar Excel
                        </Button>
                    )}
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Recogidas'} onClear={() => resetFilters(50)}>
                <PickupsFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    pickupSelected ?
                        "Completa el formulario para editar los datos de la recogida" :
                        "Completa el formulario para crear una nueva recogida"
                }
                drawerTitle={pickupSelected ? 'Editar Recogida' : 'Crear Recogida'}
                submitText={pickupSelected ? 'Guardar Recogida' : 'Crear Recogida'}
            >
                <PickupsForm
                    open={openFormDrawer}
                    onClose={() => {
                        setOpenFormDrawer(false);
                        setPickupSelected(null);
                        refetch();
                    }}
                    pickupSelected={pickupSelected}
                />
            </FormDrawerView>

            {pickupToComplete && (
                <CompletePickupModal
                    isOpen={openCompleteModal}
                    onClose={() => {
                        setOpenCompleteModal(false);
                        setPickupToComplete(null);
                        refetch();
                    }}
                    pickup={pickupToComplete}
                />
            )}

            {pickupToFail && (
                <FailPickupModal
                    isOpen={openFailModal}
                    onClose={() => {
                        setOpenFailModal(false);
                        setPickupToFail(null);
                        refetch();
                    }}
                    pickup={pickupToFail}
                />
            )}

            {openRescheduleModal && idsToReschedule.length > 0 && (
                <ReschedulePickupModal
                    isOpen={openRescheduleModal}
                    onClose={() => {
                        setOpenRescheduleModal(false);
                        setIdsToReschedule([]);
                        refetch();
                    }}
                    ids={idsToReschedule}
                />
            )}

            {openValidatePickupModal && idsToValidate.length > 0 && (
                <ValidatePickupModal
                    isOpen={openValidatePickupModal}
                    onClose={() => {
                        setOpenValidatePickupModal(false);
                        setIdsToValidate([]);
                        refetch();
                    }}
                    ids={idsToValidate}
                />
            )}

        </Page>
    );
};

export default PickupsList;